import { defineStore } from 'pinia';
import { RouteLocationNormalizedLoaded, RouteRecordName } from 'vue-router';

const VISITED_VIEWS_KEY = 'tita_admin_visited_views';

/** 从路由的 matched 中取组件名，与 keep-alive 的 include 匹配一致（sessionStorage 恢复的标签无 matched，回退 name） */
function getComponentName(view: RouteLocationNormalizedLoaded | Record<string, unknown>): RouteRecordName | undefined {
    const matched = (view as RouteLocationNormalizedLoaded).matched;
    if (Array.isArray(matched) && matched.length > 0) {
        const last = matched[matched.length - 1];
        const comp = last?.components?.default as { name?: RouteRecordName } | undefined;
        if (comp?.name != null) return comp.name;
    }
    const name = (view as RouteLocationNormalizedLoaded).name;
    return name ?? undefined;
}

const useTagsViewStore = defineStore('tags-view', {
    state: (): {
        visitedViews: RouteLocationNormalizedLoaded[];
        cachedViews: RouteRecordName[];
        iframeViews: RouteLocationNormalizedLoaded[];
    } => ({
        visitedViews: [],
        cachedViews: [],
        iframeViews: [],
    }),
    actions: {
        addView(view: RouteLocationNormalizedLoaded) {
            this.addVisitedView(view);
            this.addCachedView(view);
            this.saveVisitedViews();
        },
        addIframeView(view: RouteLocationNormalizedLoaded) {
            if (this.iframeViews.some(v => v.path === view.path)) return;
            this.iframeViews.push(
                Object.assign({}, view, {
                    title: view.meta.title || 'no-name',
                })
            );
        },
        addVisitedView(view: RouteLocationNormalizedLoaded) {
            // 使用 fullPath 做判重，确保同一路径不同 query 也能作为独立标签保存
            if (this.visitedViews.some(v => (v as any).fullPath === (view as any).fullPath)) return;
            this.visitedViews.push(
                Object.assign({}, view, {
                    title: view.meta.title || 'no-name',
                    fullPath: (view as any).fullPath || view.path,
                })
            );
        },
        addCachedView(view: RouteLocationNormalizedLoaded) {
            const name = getComponentName(view);
            if (!name || this.cachedViews.includes(name)) return;
            if (!view.meta.noCache) {
                this.cachedViews.push(name);
            }
        },
        delView(view: RouteLocationNormalizedLoaded): Promise<any> {
            return new Promise(resolve => {
                this.delVisitedView(view);
                this.delCachedView(view);
                this.saveVisitedViews();
                resolve({
                    visitedViews: [...this.visitedViews],
                    cachedViews: [...this.cachedViews],
                });
            });
        },
        delVisitedView(view: RouteLocationNormalizedLoaded) {
            return new Promise(resolve => {
                for (const [i, v] of this.visitedViews.entries()) {
                    if (v.fullPath === view.fullPath) {
                        this.visitedViews.splice(i, 1);
                        break;
                    }
                }
                this.iframeViews = this.iframeViews.filter(item => item.path !== view.path);
                this.saveVisitedViews();
                resolve([...this.visitedViews]);
            });
        },
        delIframeView(view: RouteLocationNormalizedLoaded) {
            return new Promise(resolve => {
                this.iframeViews = this.iframeViews.filter(item => item.path !== view.path);
                resolve([...this.iframeViews]);
            });
        },
        delCachedView(view: RouteLocationNormalizedLoaded | { name?: RouteRecordName }) {
            return new Promise(resolve => {
                const name = 'name' in view && view.name != null ? view.name : getComponentName(view as RouteLocationNormalizedLoaded);
                const index = name != null ? this.cachedViews.indexOf(name) : -1;
                if (index > -1) this.cachedViews.splice(index, 1);
                resolve([...this.cachedViews]);
            });
        },
        delOthersViews(view: RouteLocationNormalizedLoaded) {
            return new Promise(resolve => {
                this.delOthersVisitedViews(view);
                this.delOthersCachedViews(view);
                resolve({
                    visitedViews: [...this.visitedViews],
                    cachedViews: [...this.cachedViews],
                });
            });
        },
        delOthersVisitedViews(view: RouteLocationNormalizedLoaded) {
            return new Promise(resolve => {
                this.visitedViews = this.visitedViews.filter(v => {
                    return v.meta.affix || v.fullPath === view.fullPath;
                });
                this.iframeViews = this.iframeViews.filter(item => item.path === view.path);
                this.saveVisitedViews();
                resolve([...this.visitedViews]);
            });
        },
        delOthersCachedViews(view: RouteLocationNormalizedLoaded) {
            return new Promise(resolve => {
                const name = getComponentName(view);
                if (name != null && this.cachedViews.includes(name)) {
                    this.cachedViews = [name];
                } else {
                    this.cachedViews = [];
                }
                resolve([...this.cachedViews]);
            });
        },
        delAllViews(view?: RouteLocationNormalizedLoaded) {
            return new Promise(resolve => {
                this.delAllVisitedViews(view);
                this.delAllCachedViews(view);
                this.saveVisitedViews();
                resolve({
                    visitedViews: [...this.visitedViews],
                    cachedViews: [...this.cachedViews],
                });
            });
        },
        delAllVisitedViews(view?: RouteLocationNormalizedLoaded) {
            return new Promise(resolve => {
                const affixTags = this.visitedViews.filter(tag => tag.meta.affix);
                this.visitedViews = affixTags;
                this.iframeViews = [];
                this.saveVisitedViews();
                resolve([...this.visitedViews]);
            });
        },
        delAllCachedViews(view?: RouteLocationNormalizedLoaded) {
            return new Promise(resolve => {
                this.cachedViews = [];
                resolve([...this.cachedViews]);
            });
        },
        updateVisitedView(view: RouteLocationNormalizedLoaded) {
            for (let i = 0; i < this.visitedViews.length; i++) {
                const cur = this.visitedViews[i] as any
                if (cur.fullPath === view.fullPath) {
                    this.visitedViews[i] = Object.assign({}, this.visitedViews[i], view);
                    // 保底：确保 fullPath 存在，供 router-link key/上下文菜单使用
                    ;(this.visitedViews[i] as any).fullPath =
                        (view as any).fullPath || (view as any).path || this.visitedViews[i].path
                    this.saveVisitedViews();
                    break;
                }
            }
        },
        delRightTags(view: RouteLocationNormalizedLoaded) {
            return new Promise(resolve => {
                const index = this.visitedViews.findIndex(v => v.fullPath === view.fullPath);
                if (index === -1) {
                    resolve([...this.visitedViews]);
                    return;
                }
                this.visitedViews = this.visitedViews.filter((item, idx) => {
                    if (idx <= index || item.meta?.affix) {
                        return true;
                    }
                    const compName = getComponentName(item);
                    if (compName != null) {
                        const i = this.cachedViews.indexOf(compName);
                        if (i > -1) this.cachedViews.splice(i, 1);
                    }
                    if (item.meta?.link) {
                        const fi = this.iframeViews.findIndex(v => v.path === item.path);
                        if (fi > -1) this.iframeViews.splice(fi, 1);
                    }
                    return false;
                });
                this.saveVisitedViews();
                resolve([...this.visitedViews]);
            });
        },
        delLeftTags(view: RouteLocationNormalizedLoaded) {
            return new Promise<any>(resolve => {
                const index = this.visitedViews.findIndex(v => v.fullPath === view.fullPath);
                if (index === -1) {
                    resolve([...this.visitedViews]);
                    return;
                }
                this.visitedViews = this.visitedViews.filter((item, idx) => {
                    if (idx >= index || item.meta?.affix) {
                        return true;
                    }
                    const compName = getComponentName(item);
                    if (compName != null) {
                        const i = this.cachedViews.indexOf(compName);
                        if (i > -1) this.cachedViews.splice(i, 1);
                    }
                    if (item.meta?.link) {
                        const fi = this.iframeViews.findIndex(v => v.path === item.path);
                        if (fi > -1) this.iframeViews.splice(fi, 1);
                    }
                    return false;
                });
                this.saveVisitedViews();
                resolve([...this.visitedViews]);
            });
        },
        saveVisitedViews() {
            try {
                const list = this.visitedViews.map(v => ({
                    path: v.path,
                    fullPath: v.fullPath || v.path,
                    name: v.name,
                    query: v.query,
                    meta: v.meta,
                }));
                sessionStorage.setItem(VISITED_VIEWS_KEY, JSON.stringify(list));
            } catch (_) {
                // ignore
            }
        },
        restoreVisitedViews() {
            try {
                const raw = sessionStorage.getItem(VISITED_VIEWS_KEY);
                if (!raw) return;
                const list = JSON.parse(raw) as Array<{ path: string; fullPath: string; name?: RouteRecordName; query?: Record<string, any>; meta?: Record<string, any> }>;
                this.visitedViews = list.map(v => ({
                    ...v,
                    path: v.path,
                    fullPath: v.fullPath || v.path,
                    title: (v.meta?.title as string) || 'no-name',
                })) as RouteLocationNormalizedLoaded[];
            } catch (_) {
                // ignore
            }
        },
    },
});

export default useTagsViewStore;
